import React, { useState } from 'react';
import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, IconButton, TextField, Typography } from '@mui/material';

interface EditableTemplateHeadingProps {
  /** Template name to display */
  templateName: string;
  /** Callback when template name is saved */
  onSave: (newName: string) => Promise<void>;
  /** Whether the user can edit the template name */
  canEdit: boolean;
  /** Typography variant for the heading */
  variant?: 'h4' | 'h5';
  /** Whether to show the subtitle */
  showSubtitle?: boolean;
}

/**
 * Editable heading component for template editor
 * Allows inline editing of template name with edit/save/cancel controls
 */
export const EditableTemplateHeading: React.FC<EditableTemplateHeadingProps> = ({
  templateName,
  onSave,
  canEdit,
  variant = 'h4',
  showSubtitle = true,
}) => {

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(templateName);
  const [isSaving, setIsSaving] = useState(false);

  const handleStartEdit = () => {
    setEditValue(templateName);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditValue(templateName);
    setIsEditing(false);
  };

  const handleSave = async () => {
    const trimmedValue = editValue.trim();

    // Don't save if empty or unchanged
    if (!trimmedValue || trimmedValue === templateName) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedValue);
      setIsEditing(false);
    } catch (error) {
      // Error handling is done by parent component
      console.error('Failed to save template name:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {isEditing ? (
        <>
          <Box sx={{ flex: 1 }}>
            <TextField
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              onKeyDown={handleKeyDown}
              autoFocus
              fullWidth
              disabled={isSaving}
              placeholder="Template name"
              sx={{
                '& .MuiInputBase-root': {
                  fontSize: variant === 'h4' ? '2.125rem' : '1.5rem',
                  fontWeight: 600,
                },
              }}
             slotProps={{ htmlInput: {
                'aria-label': 'Edit template name',
                maxLength: 100,
              } }}/>
          </Box>
          <IconButton
            onClick={handleSave}
            disabled={isSaving || !editValue.trim()}
            color="primary"
            aria-label="Save template name"
            size="small"
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            onClick={handleCancel}
            disabled={isSaving}
            aria-label="Cancel editing"
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </>
      ) : (
        <>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant={variant}
              gutterBottom
              sx={{ fontWeight: 600, mb: showSubtitle ? 0.5 : 1 }}
            >
              Customizing Your{' '}
              <Box
                component="span"
                sx={theme => ({
                  fontWeight: 600,
                  textDecoration: 'underline',
                  textDecorationColor: theme.palette.primary.main,
                  textDecorationThickness: '2px',
                  textUnderlineOffset: '8px',
                })}
              >
                {templateName}
              </Box>{' '}
              Template
            </Typography>
          </Box>
          {canEdit && (
            <IconButton
              onClick={handleStartEdit}
              aria-label="Edit template name"
              size="medium"
              sx={{
                alignSelf: 'flex-start',
                mt: 0.5,
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.lighter',
                },
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </>
      )}
    </Box>
  );
};
